import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../shop/api/supabase";

const TENANT_ID = "bc87b153-b717-4234-bb79-d3d6d131ef1d";
const HOURS = Array.from({ length: 10 }, (_, i) => i + 8);
const fmtH = (h) => `${String(h).padStart(2, "0")}–${String(h + 1).padStart(2, "0")}`;
const fmtDate = (d) => new Date(d).toLocaleDateString("sr-Latn-RS", { weekday: "short", day: "numeric", month: "short" });
const fmtDateLong = (d) => new Date(d).toLocaleDateString("sr-Latn-RS", { weekday: "long", day: "numeric", month: "long" });
const fmtPrice = (n) => Number(n).toLocaleString("sr-RS") + " RSD";

function getWeekDates(offset) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + offset * 7 + i);
    return d.toISOString().split("T")[0];
  });
}

const CELL = {
  free:     { bg: "#fff8f0", border: "#e8d4b0", cursor: "pointer" },
  blocked:  { bg: "#d6cfc6", border: "#bfb8ae", cursor: "pointer" },
  booked:   { bg: "#fde8d8", border: "#f0c4a0", cursor: "pointer" },
  selected: { bg: "#3a7bd5", border: "#2a6bc5", cursor: "pointer" },
};

function cellStyle(state, selected) {
  const s = selected ? CELL.selected : CELL[state];
  return {
    background: s.bg, border: `1px solid ${s.border}`, borderRadius: 3,
    height: 30, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.68rem", fontWeight: 700, userSelect: "none",
    cursor: s.cursor, transition: "background 0.08s",
    color: selected ? "white" : state === "blocked" ? "#5a3c1e" : state === "booked" ? "#a83228" : "transparent",
  };
}

const STATUS_LABEL = { pending: "Nova", confirmed: "Potvrđena", shipped: "Poslata", delivered: "Isporučena", cancelled: "Otkazana" };
const STATUS_COLOR = { pending: "#c8a020", confirmed: "#2a7a2a", shipped: "#1a5a9a", delivered: "#2a7a2a", cancelled: "#a83228" };

export default function AdminDelivery({ onGoToOrder }) {
  const [zones, setZones]                   = useState([]);
  const [blocks, setBlocks]                 = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [loading, setLoading]               = useState(true);

  const [weekOffset, setWeekOffset]         = useState(0);
  const [selectedCells, setSelectedCells]   = useState(new Set());
  const [isDragging, setIsDragging]         = useState(false);
  const [isDraggingDate, setIsDraggingDate] = useState(false);
  const [applying, setApplying]             = useState(false);
  const [applyError, setApplyError]         = useState(null);
  const [unblocks, setUnblocks]             = useState([]);
  const [activeOrder, setActiveOrder]       = useState(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const up = () => { setIsDragging(false); setIsDraggingDate(false); };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  async function load() {
    const today = new Date().toISOString().split("T")[0];
    const [{ data: z }, { data: b }, { data: o }, { data: u }] = await Promise.all([
      supabase.from("delivery_zones").select("*").eq("tenant_id", TENANT_ID).order("name"),
      supabase.from("delivery_blocks").select("*").eq("tenant_id", TENANT_ID).gte("block_date", today).order("block_date"),
      supabase.from("orders")
        .select("id, customer, items, total, delivery_date, delivery_hour, delivery_zone_id, status")
        .eq("tenant_id", TENANT_ID).gte("delivery_date", today)
        .not("delivery_date", "is", null)
        .order("delivery_date").order("delivery_hour"),
      supabase.from("delivery_unblocks").select("*").eq("tenant_id", TENANT_ID).gte("unblock_date", today),
    ]);
    setZones(z || []);
    setBlocks(b || []);
    setDeliveryOrders(o || []);
    setUnblocks(u || []);
    setLoading(false);
  }

  // Slot = date + hour, bez zone (blokade su globalne)
  const getSlotState = useCallback((date, hour) => {
    if (deliveryOrders.some((o) => o.delivery_date === date && o.delivery_hour === hour))
      return "booked";
    // Nedelja: proveravamo PRE delivery_blocks jer unblock ima apsolutni prioritet
    // (stare delivery_blocks za nedelju se ignorišu)
    const dow = new Date(date + "T12:00:00").getDay();
    if (dow === 0) {
      const isUnblocked = unblocks.some(
        (u) => u.unblock_date === date && (u.unblock_hour === hour || u.unblock_hour === null)
      );
      return isUnblocked ? "free" : "blocked";
    }
    // Ostali dani — proveri delivery_blocks
    if (blocks.some((b) => b.block_date === date && (b.block_hour === null || b.block_hour === hour)))
      return "blocked";
    return "free";
  }, [deliveryOrders, blocks, unblocks]);

  const getOrdersForSlot = useCallback((date, hour) =>
    deliveryOrders.filter((o) => o.delivery_date === date && o.delivery_hour === hour)
  , [deliveryOrders]);

  const handleSlotMouseDown = (date, hour) => {
    if (getSlotState(date, hour) === "booked") return;
    setIsDragging(true);
    const key = `${date}|${hour}`;
    setSelectedCells((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  };

  const handleSlotMouseEnter = (date, hour) => {
    if (!isDragging || getSlotState(date, hour) === "booked") return;
    setSelectedCells((prev) => { const next = new Set(prev); next.add(`${date}|${hour}`); return next; });
  };

  const selectDay = (date, addOnly = false) => {
    const keys = HOURS.filter((h) => getSlotState(date, h) !== "booked").map((h) => `${date}|${h}`);
    if (keys.length === 0) return;
    setSelectedCells((prev) => {
      const next = new Set(prev);
      const allSelected = keys.every((k) => next.has(k));
      if (allSelected && !addOnly) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const isSunday = (date) => new Date(date + "T12:00:00").getDay() === 0;

  const applyAction = async (action) => {
    setApplying(true);
    setApplyError(null);
    const cells = Array.from(selectedCells).map((key) => {
      const [date, hour] = key.split("|");
      return { date, hour: parseInt(hour) };
    });

    try {
      if (action === "block") {
        // Nedelja koja je bila ručno otključana — ukloni unblock da se vrati auto-blokada
        const sundayCells = cells.filter((c) => isSunday(c.date));
        for (const c of sundayCells) {
          const { error } = await supabase.from("delivery_unblocks").delete()
            .eq("tenant_id", TENANT_ID).eq("unblock_date", c.date).eq("unblock_hour", c.hour);
          if (error) throw new Error(`Unblocks delete: ${error.message}`);
        }
        // Ostali slobodni slotovi — dodaj u delivery_blocks
        const toBlock = cells.filter((c) => !isSunday(c.date) && getSlotState(c.date, c.hour) === "free");
        if (toBlock.length > 0) {
          const { error } = await supabase.from("delivery_blocks").insert(
            toBlock.map((c) => ({ tenant_id: TENANT_ID, zone_id: null, block_date: c.date, block_hour: c.hour }))
          );
          if (error) throw new Error(`Blocks insert: ${error.message}`);
        }
      } else {
        // UNBLOCK
        // Nedeljni slotovi — upiši u delivery_unblocks
        const sundayCells = cells.filter((c) => isSunday(c.date));
        if (sundayCells.length > 0) {
          const { error } = await supabase.from("delivery_unblocks").upsert(
            sundayCells.map((c) => ({ tenant_id: TENANT_ID, unblock_date: c.date, unblock_hour: c.hour })),
            { onConflict: "tenant_id,unblock_date,unblock_hour" }
          );
          if (error) throw new Error(`Unblocks upsert: ${error.message}`);
        }
        // Ostali blokirani slotovi — ukloni iz delivery_blocks
        const nonSundayCells = cells.filter((c) => !isSunday(c.date));
        if (nonSundayCells.length > 0) {
          const byDate = {};
          nonSundayCells.forEach((c) => { if (!byDate[c.date]) byDate[c.date] = []; byDate[c.date].push(c.hour); });
          for (const [date, hours] of Object.entries(byDate)) {
            const { error: e1 } = await supabase.from("delivery_blocks").delete()
              .eq("tenant_id", TENANT_ID).eq("block_date", date).is("block_hour", null);
            if (e1) throw new Error(`Blocks delete full-day: ${e1.message}`);
            const { error: e2 } = await supabase.from("delivery_blocks").delete()
              .eq("tenant_id", TENANT_ID).eq("block_date", date).in("block_hour", hours);
            if (e2) throw new Error(`Blocks delete hours: ${e2.message}`);
          }
        }
      }
    } catch (err) {
      console.error("applyAction error:", err);
      setApplyError(err.message);
      setApplying(false);
      return;
    }

    setSelectedCells(new Set());
    setApplying(false);
    load();
  };


  if (loading) return <div className="admin-loading">Učitavanje...</div>;

  const weekDates = getWeekDates(weekOffset);
  const today = new Date().toISOString().split("T")[0];
  const hasSelection = selectedCells.size > 0;
  const selFree    = hasSelection && Array.from(selectedCells).some((k) => { const [d,h] = k.split("|"); return getSlotState(d, parseInt(h)) === "free"; });
  const selBlocked = hasSelection && Array.from(selectedCells).some((k) => { const [d,h] = k.split("|"); return getSlotState(d, parseInt(h)) === "blocked"; });

  return (
    <div className="admin-delivery">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Dostava</h2>
      </div>

      {applyError && (
        <div style={{ background: "#fde8e8", border: "1px solid #e08080", borderRadius: 4, padding: "0.6rem 1rem", marginBottom: "0.8rem", fontSize: "0.82rem", color: "#a83228", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>⚠ Greška: {applyError}</span>
          <button onClick={() => setApplyError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#a83228", fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.8rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center", fontSize: "0.78rem", color: "#5a3c1e" }}>
          {[["Slobodan", CELL.free], ["Blokiran", CELL.blocked], ["Zauzet", CELL.booked], ["Selektovan", CELL.selected]].map(([label, c]) => (
            <span key={label} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: c.bg, border: `1px solid ${c.border}`, display: "inline-block" }} />
              {label}
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {hasSelection && (
            <>
              {selFree && (
                <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => applyAction("block")} disabled={applying}>
                  {applying ? "..." : `Blokiraj (${selectedCells.size})`}
                </button>
              )}
              {selBlocked && (
                <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => applyAction("unblock")} disabled={applying}>
                  {applying ? "..." : `Oslobodi (${selectedCells.size})`}
                </button>
              )}
              <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setSelectedCells(new Set())}>✕</button>
            </>
          )}
        </div>
      </div>

      {/* Week nav */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "0.8rem" }}>
        <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setWeekOffset((o) => Math.max(0, o - 1))} disabled={weekOffset === 0}>← Prethodna</button>
        <span style={{ fontSize: "0.82rem", color: "#5a3c1e" }}>{fmtDate(weekDates[0])} – {fmtDate(weekDates[6])}</span>
        <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setWeekOffset((o) => o + 1)}>Sledeća →</button>
      </div>

      {/* Grid — jedan red po datumu, kolone = sati */}
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 660, border: "1px solid #e8d4b0", borderRadius: 6, overflow: "hidden" }}>

          {/* Hour header */}
          <div style={{ display: "grid", gridTemplateColumns: "100px repeat(10, 1fr)", gap: 2, padding: 2, background: "#f0e4cf", borderBottom: "1px solid #e8d4b0" }}>
            <div />
            {HOURS.map((h) => (
              <div key={h} style={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 700, color: "#5a3c1e" }}>
                {fmtH(h)}h
              </div>
            ))}
          </div>

          {/* Date rows */}
          {weekDates.map((d, di) => (
            <div key={d} style={{ display: "grid", gridTemplateColumns: "100px repeat(10, 1fr)", gap: 2, padding: 2, background: d === today ? "#fef5ec" : "#fdf8f2", borderTop: di > 0 ? "1px solid #e8d4b0" : "none" }}>
              <div
                style={{ fontSize: "0.75rem", fontWeight: d === today ? 700 : 500, color: d === today ? "#a83228" : "#2a1a0a", display: "flex", alignItems: "center", paddingLeft: 6, cursor: "pointer", userSelect: "none" }}
                onMouseDown={() => { setIsDraggingDate(true); selectDay(d); }}
                onMouseEnter={() => { if (isDraggingDate) selectDay(d, true); }}
                onContextMenu={(e) => e.preventDefault()}
                title="Klikni da selektuješ ceo dan"
              >
                {fmtDate(d)}
              </div>
              {HOURS.map((h) => {
                const key     = `${d}|${h}`;
                const sel     = selectedCells.has(key);
                const orders  = getOrdersForSlot(d, h);
                const state   = getSlotState(d, h);
                return (
                  <div
                    key={h}
                    style={cellStyle(state, sel)}
                    onMouseDown={() => handleSlotMouseDown(d, h)}
                    onMouseEnter={() => handleSlotMouseEnter(d, h)}
                    onClick={() => state === "booked" && setActiveOrder(orders)}
                    onContextMenu={(e) => e.preventDefault()}
                    title={state === "booked" ? orders.map((o) => o.customer?.ime).join(", ") + " — klikni" : state === "blocked" ? "Blokirano" : "Slobodno"}
                  >
                    {state === "booked" && (orders[0]?.customer?.opstina || "●")}
                    {state === "blocked" && !sel && "✕"}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Order modal — activeOrder je niz porudžbina za taj slot ── */}
      {activeOrder && (
        <div className="admin-modal__backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveOrder(null); }}>
          <div className="admin-modal" style={{ maxWidth: 500 }}>
            <button onClick={() => setActiveOrder(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#5a3c1e" }}>✕</button>
            <p className="admin-modal__title">
              {fmtDateLong(activeOrder[0].delivery_date)} · {String(activeOrder[0].delivery_hour).padStart(2,"0")}:00h
            </p>
            <p className="admin-modal__sub">{activeOrder.length} {activeOrder.length === 1 ? "porudžbina" : "porudžbine"}</p>

            {activeOrder.map((order) => (
              <div key={order.id} style={{ borderTop: "1px solid #e8d4b0", paddingTop: "0.8rem", marginTop: "0.8rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#2a1a0a" }}>{order.customer?.ime}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: STATUS_COLOR[order.status] }}>{STATUS_LABEL[order.status]}</span>
                    <button
                      className="admin-btn admin-btn--sm admin-btn--ghost"
                      onClick={() => { setActiveOrder(null); onGoToOrder?.(order.id); }}
                    >
                      Uredi →
                    </button>
                  </div>
                </div>
                {[["Telefon", order.customer?.telefon], ["Email", order.customer?.email], ["Adresa", order.customer?.adresa], ["Opština", order.customer?.opstina]].map(([l, v]) => v ? (
                  <div key={l} style={{ display: "flex", gap: "0.5rem", fontSize: "0.83rem", marginBottom: "0.15rem" }}>
                    <span style={{ color: "#5a3c1e", minWidth: 56 }}>{l}:</span>
                    <span style={{ color: "#2a1a0a" }}>{v}</span>
                  </div>
                ) : null)}
                {order.customer?.napomena && (
                  <div style={{ marginTop: "0.3rem", fontSize: "0.8rem", color: "#5a3c1e", background: "#f0e4cf", padding: "0.3rem 0.5rem", borderRadius: 3 }}>💬 {order.customer.napomena}</div>
                )}
                <div style={{ marginTop: "0.5rem" }}>
                  {(order.items || []).map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", color: "#2a1a0a", padding: "0.2rem 0", borderBottom: "1px solid #f0e8dc" }}>
                      <span>{item.title} × {item.kolicina}</span>
                      <span style={{ color: "#a83228", fontWeight: 600 }}>{item.cena}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88rem", fontWeight: 700, marginTop: "0.4rem" }}>
                    <span>Ukupno</span>
                    <span style={{ color: "#a83228" }}>{fmtPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
